import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Paper, TextField, Typography, List, ListItem, ListItemText, Divider, ButtonGroup, Button, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import EditBookingModal from './EditBookingModal';
import IndividualBookingForm from './IndividualBookingForm';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { authaltFetch, AUTHALT_API_BASE } from '../services/authaltApi';

// API Configuration - token fetched dynamically from backend
const GUEST_API_BASE = AUTHALT_API_BASE;

const toISODate = (s) => {
  if (!s) return null;
  const str = String(s).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  const m = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    const d = String(m[1]).padStart(2, '0');
    const mo = String(m[2]).padStart(2, '0');
    const y = String(m[3]).length === 2 ? `20${m[3]}` : String(m[3]);
    return `${y}-${mo}-${d}`;
  }
  const t = Date.parse(str);
  if (!Number.isNaN(t)) return new Date(t).toISOString().split('T')[0];
  return null;
};

const getGuestStayDates = (guest) => ({
  checkIn: toISODate(guest?.checkIn || guest?.checkin || guest?.startDate || guest?.arrivalDate),
  checkOut: toISODate(guest?.checkOut || guest?.checkout || guest?.endDate || guest?.departureDate),
});

const buildDateRange = (startIso, endIso) => {
  const [sy, sm, sd] = startIso.split('-').map(Number);
  const [ey, em, ed] = endIso.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return null;
  const days = [];
  const d = new Date(start);
  while (d <= end) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    days.push(`${y}-${m}-${day}`);
    d.setDate(d.getDate() + 1);
  }
  return days;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const days = [];
  for (let i = -3; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
};

const normalizeGuestRecord = (guest) => ({
  id: guest?.guestId || guest?.id || '',
  guestId: guest?.guestId || guest?.id || '',
  name: guest?.guestName || guest?.name || '',
  room: guest?.room || '',
  checkIn: guest?.checkIn || guest?.checkin || guest?.startDate || guest?.arrivalDate || '',
  checkOut: guest?.checkOut || guest?.checkout || guest?.endDate || guest?.departureDate || '',
});

const ITINERARY_PENDING_GUEST_KEY = 'itinerary-pending-guest';

const Itinerary = () => {
  const [guestQuery, setGuestQuery] = useState('');
  const [roomQuery, setRoomQuery] = useState('');
  const [searchMode, setSearchMode] = useState(() => localStorage.getItem('itinerarySearchMode') || 'guest'); // 'guest' or 'room'
  const [dateRange, setDateRange] = useState(() => getDefaultDateRange());
  const [bookingsByDate, setBookingsByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [config, setConfig] = useState({ rooms: {}, treatments: [], therapists: [] });
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('itineraryView') || 'normal');
  const [guests, setGuests] = useState([]);
  const [guestLookupReady, setGuestLookupReady] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [availableActivitiesByDate, setAvailableActivitiesByDate] = useState({});
  const [showAvailableActivitiesModal, setShowAvailableActivitiesModal] = useState(false);
  const [selectedSlotInfo, setSelectedSlotInfo] = useState(null);
  const [activityDurations, setActivityDurations] = useState({});
  const [showAddService, setShowAddService] = useState(false);
  const [addServiceLoading, setAddServiceLoading] = useState(false);
  const [addServiceError, setAddServiceError] = useState('');
  const [addServicePreview, setAddServicePreview] = useState(null);

  const applyGuestSelection = useCallback((guest) => {
    if (!guest) return;
    const normalized = normalizeGuestRecord(guest);
    setSearchMode('guest');
    setSelectedGuest(normalized);
    setGuestQuery(normalized.name || normalized.guestId || normalized.id || '');
  }, []);

  const removeBooking = async (booking) => {
    const id = booking?.bookingId || booking?.id;
    const date = booking?.date;
    if (!id || !date) return;
    const label = `${booking.treatment || 'this service'} on ${date} at ${booking.startTime || ''}`.trim();
    if (!window.confirm(`Remove ${label}?`)) return;
    const resp = await apiDelete(`/api/bookings/${date}/${id}`);
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      window.alert(body.detail || 'Could not remove this service');
      return;
    }
    setSelectedBooking(null);
    await refreshBookings();
  };

  const openAddService = () => {
    setAddServiceError('');
    setAddServicePreview(null);
    setShowAddService(true);
  };

  const submitExtraService = async (formData) => {
    setAddServiceLoading(true);
    setAddServiceError('');
    setAddServicePreview(null);
    try {
      const avoidTimes = (formData.avoidTimes || []).map((time) => {
        const [hours, minutes] = String(time).split(':');
        return parseInt(hours, 10) * 100 + parseInt(minutes, 10);
      });
      const requestData = {
        guestName: formData.guestName || selectedGuest?.name || null,
        guestId: formData.guestId || selectedGuest?.guestId || selectedGuest?.id || null,
        guestRoom: formData.guestRoom ?? null,
        treatment: formData.treatment,
        date: formData.date,
        avoidTimes,
        preferredTime: formData.preferredTime || null,
        preferredTimeOfDay: formData.preferredTimeOfDay === '' ? null : formData.preferredTimeOfDay,
        therapistPreference: formData.therapistPreference || null,
        roomPreference: formData.roomPreference || null,
        genderPreference: formData.genderPreference || null,
        guestGender: formData.guestGender || null,
        allowAfterOperationalEnd: !!formData.allowAfterOperationalEnd,
        generalInstruction: formData.generalInstruction || '',
        treatmentInstruction: formData.treatmentInstruction || '',
      };
      const previewResponse = await apiPost('/api/schedule/preview', requestData);
      const previewData = await previewResponse.json();
      if (!previewResponse.ok || previewData.error) {
        if (
          previewData.error_type === 'AFTER_HOURS_CONFIRMATION_REQUIRED'
          && !requestData.allowAfterOperationalEnd
        ) {
          const confirmed = window.confirm(`${previewData.error}\n\nBook this after-hours slot anyway?`);
          if (confirmed) {
            await submitExtraService({ ...formData, allowAfterOperationalEnd: true });
          }
          return;
        }
        setAddServiceError(previewData.error || previewData.detail || 'No slot found for this service');
        return;
      }
      setAddServicePreview(previewData);
    } catch (error) {
      setAddServiceError(error.message || 'Could not find a slot for this service');
    } finally {
      setAddServiceLoading(false);
    }
  };

  const confirmExtraService = async () => {
    if (!addServicePreview) return;
    setAddServiceLoading(true);
    setAddServiceError('');
    try {
      const bookingData = {
        treatment: addServicePreview.treatment,
        date: addServicePreview.date,
        startTime: addServicePreview.startTime,
        endTime: addServicePreview.endTime,
        guestName: addServicePreview.guestName,
        guestId: addServicePreview.guestId,
        therapist: addServicePreview.therapist,
        therapists: addServicePreview.therapists || [],
        room: addServicePreview.room,
        generalInstruction: addServicePreview.generalInstruction || '',
        treatmentInstruction: addServicePreview.treatmentInstruction || '',
      };
      const confirmResponse = await apiPost('/api/schedule/confirm', bookingData);
      const confirmData = await confirmResponse.json().catch(() => ({}));
      if (!confirmResponse.ok || confirmData.error) {
        setAddServiceError(confirmData.detail || confirmData.error || 'Could not add this service');
        return;
      }
      setShowAddService(false);
      setAddServicePreview(null);
      await refreshBookings();
    } catch (error) {
      setAddServiceError(error.message || 'Could not add this service');
    } finally {
      setAddServiceLoading(false);
    }
  };

  const refreshBookings = useCallback(async () => {
    const entries = await Promise.all(
      dateRange.map(async (d) => {
        const res = await apiGet(`/api/bookings/${d}`);
        const data = await res.json();
        return [d, Array.isArray(data) ? data : []];
      })
    );
    const map = {};
    for (const [d, list] of entries) map[d] = list;
    setBookingsByDate(map);
  }, [dateRange]);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await apiGet('/api/config');
        const data = await res.json();
        setConfig({
          rooms: data.rooms || {},
          treatments: data.treatments || [],
          therapists: Object.values(data.therapists || {}),
        });
      } catch {}
    };
    loadConfig();
  }, []);

  // Load guests for autocomplete lookup
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        // Fetch all guest types using the new API endpoints with POST
        const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
        let allGuestsData = [];
        
        for (const type of types) {
          try {
            const res = await authaltFetch('/guestlistdatav3', {
              type,
              index: 'atmantan'
            });
            if (res.ok) {
              const response = await res.json();
              const categoryKey = type === 'InHouse' ? 'inHouse' :
                                 type === 'TodaysArrival' ? 'todaysArrival' :
                                 type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
              const guestsFromType = response[categoryKey]?.data || [];
              allGuestsData = allGuestsData.concat(guestsFromType);
            }
          } catch (e) {
            console.error(`Failed to fetch ${type}:`, e);
          }
        }
        
        // Remove duplicates by guestId
        const uniqueGuests = Array.from(
          new Map(allGuestsData.map(g => [g.guestId || g.id, g])).values()
        );
        
        // Normalize to match expected format
        const list = uniqueGuests.map((g) => ({
          id: g.guestId || g.id || '',
          name: g.guestName || g.name || '',
          room: g.room || '',
          guestId: g.guestId || '',
          bookingId: g.bookingId || '',
          gender: g.gender || '',
          checkIn: g.checkIn || g.checkin || g.startDate || g.arrivalDate || '',
          checkOut: g.checkOut || g.checkout || g.endDate || g.departureDate || '',
        }));
        
        // Filter out guests without names and sort alphabetically by name
        const sorted = list
          .filter((g) => g.name && g.name.trim() !== '') // Only show guests with names
          .sort((a, b) => {
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        setGuests(sorted); 
      } catch (e) {
        setGuests([]);
      } finally {
        setGuestLookupReady(true);
      }
    };
    fetchGuests();
  }, []);

  useEffect(() => {
    localStorage.setItem('itineraryView', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('itinerarySearchMode', searchMode);
  }, [searchMode]);

  // Allow external navigation to set the guest filter (Guests module → Go to Itinerary)
  useEffect(() => {
    const handler = (e) => {
      const { guest, guestName, guestId } = e.detail || {};
      if (guest) {
        applyGuestSelection(guest);
        return;
      }
      if (guestName || guestId) {
        applyGuestSelection({ guestName, guestId, id: guestId });
      }
    };
    window.addEventListener('set-itinerary-guest', handler);
    return () => window.removeEventListener('set-itinerary-guest', handler);
  }, [applyGuestSelection]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(ITINERARY_PENDING_GUEST_KEY);
      if (!raw) return;
      sessionStorage.removeItem(ITINERARY_PENDING_GUEST_KEY);
      const payload = JSON.parse(raw);
      if (payload?.guest) {
        applyGuestSelection(payload.guest);
      } else if (payload?.guestName || payload?.guestId) {
        applyGuestSelection(payload);
      }
    } catch (error) {
      console.error('Failed to restore pending itinerary guest:', error);
    }
  }, [applyGuestSelection]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const entries = await Promise.all(
          dateRange.map(async (d) => {
            const res = await apiGet(`/api/bookings/${d}`);
            const data = await res.json();
            return [d, Array.isArray(data) ? data : []];
          })
        );
        const map = {};
        for (const [d, list] of entries) map[d] = list;
        setBookingsByDate(map);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [dateRange]);

  // Fetch available activities when a guest is selected
  const fetchAvailableActivitiesForRange = useCallback(async () => {
    if (!dateRange.length || !selectedGuest) {
      setAvailableActivitiesByDate({});
      return;
    }
    
    try {
      const startDate = dateRange[0];
      const endDate = dateRange[dateRange.length - 1];
      const response = await apiGet(`/api/activities/timetable/available?start_date=${startDate}&end_date=${endDate}`);
      const data = await response.json();
      setAvailableActivitiesByDate(data);
    } catch (error) {
      console.error('Error fetching available activities:', error);
      setAvailableActivitiesByDate({});
    }
  }, [dateRange, selectedGuest]);

  useEffect(() => { 
    fetchAvailableActivitiesForRange(); 
  }, [fetchAvailableActivitiesForRange]);

  // Get unique room numbers from guests
  const availableRooms = useMemo(() => {
    const rooms = [...new Set(guests.map(g => String(g.room || '')).filter(r => r.trim()))];
    return rooms.sort((a, b) => {
      // Try to sort numerically if possible
      const aNum = parseInt(a);
      const bNum = parseInt(b);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return String(a).localeCompare(String(b));
    });
  }, [guests]);

  // Get guests in the selected room
  const guestsInRoom = useMemo(() => {
    if (searchMode !== 'room' || !roomQuery || !(String(roomQuery).trim())) return [];
    const roomNo = String(roomQuery).trim().toLowerCase();
    return guests.filter(g => String(g.room || '').toLowerCase() === roomNo);
  }, [searchMode, roomQuery, guests]);

  // Scope the calendar/list to the guest's stay dates when a guest or room is selected
  useEffect(() => {
    if (searchMode === 'guest' && selectedGuest) {
      const { checkIn, checkOut } = getGuestStayDates(selectedGuest);
      if (checkIn) {
        const end = checkOut || checkIn;
        const range = buildDateRange(checkIn, end);
        if (range?.length) {
          setDateRange(range);
          return;
        }
      }
    }

    if (searchMode === 'room' && roomQuery && guestsInRoom.length > 0) {
      let minIn = null;
      let maxOut = null;
      for (const g of guestsInRoom) {
        const { checkIn, checkOut } = getGuestStayDates(g);
        if (checkIn && (!minIn || checkIn < minIn)) minIn = checkIn;
        if (checkOut && (!maxOut || checkOut > maxOut)) maxOut = checkOut;
      }
      if (minIn) {
        const range = buildDateRange(minIn, maxOut || minIn);
        if (range?.length) {
          setDateRange(range);
          return;
        }
      }
    }

    setDateRange(getDefaultDateRange());
  }, [searchMode, selectedGuest, roomQuery, guestsInRoom]);

  // Match typed guest name to guest record for stay-date lookup
  useEffect(() => {
    if (searchMode !== 'guest') return;
    const q = guestQuery.trim().toLowerCase();
    if (!q) {
      setSelectedGuest(null);
      return;
    }
    if (
      selectedGuest &&
      (
        (selectedGuest.name || '').toLowerCase() === q ||
        (selectedGuest.id || '').toLowerCase() === q ||
        (selectedGuest.guestId || '').toLowerCase() === q
      )
    ) {
      return;
    }
    const match = guests.find(
      (g) =>
        (g.name || '').toLowerCase() === q ||
        (g.id || '').toLowerCase() === q ||
        (g.guestId || '').toLowerCase() === q ||
        (g.name || '').toLowerCase().includes(q) ||
        (g.id || '').toLowerCase().includes(q) ||
        (g.guestId || '').toLowerCase().includes(q)
    );
    if (match) setSelectedGuest(match);
  }, [guestQuery, guests, searchMode, selectedGuest]);

  const filtered = useMemo(() => {
    const out = {};
    
    if (searchMode === 'guest') {
      const q = guestQuery.trim().toLowerCase();
      const guestIdFilter = (
        selectedGuest?.guestId ||
        selectedGuest?.id ||
        ''
      ).toLowerCase();
      for (const d of dateRange) {
        const list = bookingsByDate[d] || [];
        if (!q && !guestIdFilter) {
          out[d] = list;
          continue;
        }
        out[d] = list.filter((b) => {
          const bookingGuestId = (b.guestId || '').toLowerCase();
          const bookingGuestName = (b.guestName || '').toLowerCase();
          if (guestIdFilter && bookingGuestId === guestIdFilter) return true;
          if (q && (bookingGuestName.includes(q) || bookingGuestId.includes(q))) return true;
          return false;
        });
      }
    } else if (searchMode === 'room') {
      // Filter by room number - show all guests in that room
      const roomNo = roomQuery ? String(roomQuery).trim().toLowerCase() : '';
      for (const d of dateRange) {
        const list = bookingsByDate[d] || [];
        if (roomNo) {
          // Match by room number in bookings
          out[d] = list.filter(b => String(b.room || '').toLowerCase() === roomNo);
        } else {
          out[d] = list;
        }
      }
    }
    
    return out;
  }, [searchMode, guestQuery, roomQuery, bookingsByDate, dateRange, selectedGuest]);

  const SLOT_MINUTES = viewMode === 'compact' ? 30 : 15;
  const SLOT_HEIGHT = viewMode === 'compact' ? 28 : 40; // px per row

  const colorForTreatment = (name) => {
    const str = String(name || '');
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    const hue = Math.abs(hash) % 360;
    const bg = `hsl(${hue}, 80%, ${viewMode === 'compact' ? 93 : 92}%)`;
    const bd = `hsl(${hue}, 60%, ${viewMode === 'compact' ? 65 : 62}%)`;
    return { bg, bd };
  };

  return (
    <Box sx={{ display: 'flex', height: '100%', p: 2, gap: 2, bgcolor: '#f6f6f6' }}>
      <Paper sx={{ width: 340, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Itinerary</Typography>
        
        {/* Search Mode Toggle */}
        <ButtonGroup fullWidth size="small" sx={{ mb: 2 }}>
          <Button 
            onClick={() => setSearchMode('guest')} 
            variant={searchMode === 'guest' ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: searchMode === 'guest' ? '#E4C984' : 'transparent',
              color: searchMode === 'guest' ? '#171717' : '#666',
              borderColor: '#E4C984',
              '&:hover': {
                backgroundColor: searchMode === 'guest' ? '#d4b974' : 'rgba(228, 201, 132, 0.08)',
                borderColor: '#E4C984'
              }
            }}
          >
            By Guest
          </Button>
          <Button 
            onClick={() => setSearchMode('room')} 
            variant={searchMode === 'room' ? 'contained' : 'outlined'}
            sx={{
              backgroundColor: searchMode === 'room' ? '#E4C984' : 'transparent',
              color: searchMode === 'room' ? '#171717' : '#666',
              borderColor: '#E4C984',
              '&:hover': {
                backgroundColor: searchMode === 'room' ? '#d4b974' : 'rgba(228, 201, 132, 0.08)',
                borderColor: '#E4C984'
              }
            }}
          >
            By Room
          </Button>
        </ButtonGroup>

        {/* Guest Search */}
        {searchMode === 'guest' && (
          <Autocomplete
            options={guests}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.name || option.id || '';
            }}
            value={guests.find(g => g.name === guestQuery || g.id === guestQuery) || null}
            onChange={(e, newValue) => {
              if (newValue) {
                setGuestQuery(newValue.name || newValue.id || '');
                setSelectedGuest(newValue);
              } else {
                setGuestQuery('');
                setSelectedGuest(null);
              }
            }}
            onInputChange={(e, newInputValue) => {
              // Allow free text input as well
              if (e && e.type === 'change') {
                setGuestQuery(newInputValue);
              }
            }}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Guest name or ID"
                placeholder="e.g. John or GU001"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id || option.name}>
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {option.id}{option.room ? ` • Room ${option.room}` : ''}
                  </Typography>
                </Box>
              </li>
            )}
          />
        )}

        {/* Room Search */}
        {searchMode === 'room' && (
          <Autocomplete
            options={availableRooms}
            getOptionLabel={(option) => String(option)}
            value={roomQuery ? String(roomQuery) : null}
            onChange={(e, newValue) => {
              setRoomQuery(newValue ? String(newValue) : '');
            }}
            onInputChange={(e, newInputValue) => {
              if (e && e.type === 'change') {
                setRoomQuery(newInputValue || '');
              }
            }}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Room number"
                placeholder="e.g. 101"
              />
            )}
          />
        )}

        {/* Show guests in selected room */}
        {searchMode === 'room' && guestsInRoom.length > 0 && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fef3c7', borderRadius: 1, border: '1px solid #fde68a' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e', display: 'block', mb: 0.5 }}>
              Guests in Room {String(roomQuery)}:
            </Typography>
            {guestsInRoom.map((guest, idx) => (
              <Typography key={guest.id || idx} variant="caption" sx={{ color: '#78350f', display: 'block' }}>
                • {guest.name} {guest.id ? `(${guest.id})` : ''}
              </Typography>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />
        {searchMode === 'guest' && selectedGuest && (
          <Button
            fullWidth
            variant="contained"
            onClick={openAddService}
            sx={{ mb: 2, bgcolor: '#E4C984', color: '#171717', '&:hover': { bgcolor: '#d4b974' } }}
          >
            Add service
          </Button>
        )}
        <List dense>
          {dateRange.map((d) => {
            const items = filtered[d] || [];
            // Sort items by startTime chronologically
            const sortedItems = [...items].sort((a, b) => {
              const timeA = a.startTime || '00:00';
              const timeB = b.startTime || '00:00';
              return timeA.localeCompare(timeB);
            });
            return (
              <Box key={d} sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}
                </Typography>
                {sortedItems.length === 0 ? (
                  <Typography variant="caption" color="text.disabled">No bookings</Typography>
                ) : (
                  sortedItems.map((b) => (
                    <ListItem
                      key={b.bookingId || `${b.guestName}-${b.startTime}-${b.room}`}
                      sx={{ py: 0.5, pr: '148px', alignItems: 'flex-start' }}
                      onClick={() => setSelectedBooking({ ...b, date: d })}
                      button
                      secondaryAction={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBooking({ ...b, date: d });
                            }}
                            sx={{ color: '#171717', minWidth: 40 }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeBooking({ ...b, date: d });
                            }}
                            sx={{ color: '#b91c1c', minWidth: 56 }}
                          >
                            Remove
                          </Button>
                        </Box>
                      }
                    >
                      <ListItemText
                        primary={`${b.startTime} ${b.treatment}`}
                        secondary={`${b.guestName} • Room ${b.room} • ${b.therapist}`}
                        primaryTypographyProps={{ fontSize: '0.85rem', sx: { whiteSpace: 'normal' } }}
                        secondaryTypographyProps={{ fontSize: '0.75rem', sx: { whiteSpace: 'normal' } }}
                      />
                    </ListItem>
                  ))
                )}
              </Box>
            );
          })}
        </List>
      </Paper>

      <Paper sx={{ flex: 1, p: 2, overflow: 'auto', bgcolor: '#fff', border: '1px solid #e5e7eb', borderRadius: 2, boxShadow: 'rgba(145, 158, 171, 0.12) 0px 12px 24px -4px, rgba(145, 158, 171, 0.2) 0px 0px 2px 0px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Calendar</Typography>
            {/* Show guest names when filtering by room */}
            {searchMode === 'room' && guestsInRoom.length > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                Room {String(roomQuery)}: {guestsInRoom.map(g => g.name).join(', ')}
              </Typography>
            )}
          </Box>
          <ButtonGroup size="small" variant="outlined">
            <Button onClick={() => setViewMode('normal')} variant={viewMode === 'normal' ? 'contained' : 'outlined'}>Normal</Button>
            <Button onClick={() => setViewMode('compact')} variant={viewMode === 'compact' ? 'contained' : 'outlined'}>Compact</Button>
          </ButtonGroup>
        </Box>
        <Box sx={{ overflow: 'auto', position: 'relative', maxHeight: 'calc(100vh - 220px)' }}>
          <table className="w-full border-collapse relative">
            <thead>
              <tr>
                <th className="p-2 border sticky left-0 top-0 bg-white z-30">Time</th>
                {dateRange.map((d) => (
                  <th key={d} className="p-2 border min-w-[180px] sticky top-0 bg-white z-20">
                    <div className="font-semibold">{new Date(d).toLocaleDateString('en-GB', { weekday: 'short' })}</div>
                    <div className="text-xs text-gray-500">{new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: Math.floor(((19 - 6) * 60) / SLOT_MINUTES) + 1 }).map((_, idx) => {
                const minutes = 6 * 60 + idx * SLOT_MINUTES;
                const h = Math.floor(minutes / 60);
                const m = minutes % 60;
                const label = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
                return (
                  <tr key={label} style={{ height: `${SLOT_HEIGHT}px` }}>
                    <td className="p-1 border text-sm sticky left-0 bg-white z-20">{label}</td>
                    {dateRange.map((d) => {
                        const dayBookings = filtered[d] || [];
                        const bookingsAtSlot = dayBookings
                          .filter(b => b.startTime && b.endTime)
                          .filter(b => {
                            const [bh, bm] = b.startTime.split(':').map(Number);
                            const bMin = bh * 60 + bm;
                            return Math.floor(bMin / SLOT_MINUTES) * SLOT_MINUTES === minutes;
                          });
                        
                        // Check for available activities at this slot
                        const availableActivitiesForDay = availableActivitiesByDate[d] || [];
                        
                        // Filter activities at this slot that DON'T overlap with any guest booking
                        const activitiesAtSlot = availableActivitiesForDay.filter(activity => {
                          const [ah, am] = activity.startTime.split(':').map(Number);
                          const aMin = ah * 60 + am;
                          const isAtThisSlot = Math.floor(aMin / SLOT_MINUTES) * SLOT_MINUTES === minutes;
                          if (!isAtThisSlot) return false;
                          
                          // Check if activity overlaps with any of the guest's bookings
                          const [actStartH, actStartM] = activity.startTime.split(':').map(Number);
                          const [actEndH, actEndM] = activity.endTime.split(':').map(Number);
                          const actStart = actStartH * 60 + actStartM;
                          const actEnd = actEndH * 60 + actEndM;
                          
                          const hasOverlap = dayBookings.some(booking => {
                            if (!booking.startTime || !booking.endTime) return false;
                            const [bStartH, bStartM] = booking.startTime.split(':').map(Number);
                            const [bEndH, bEndM] = booking.endTime.split(':').map(Number);
                            const bookStart = bStartH * 60 + bStartM;
                            const bookEnd = bEndH * 60 + bEndM;
                            return !(actEnd <= bookStart || actStart >= bookEnd);
                          });
                          
                          return !hasOverlap;
                        });
                        
                        const hasBookings = bookingsAtSlot.length > 0;
                        const hasAvailableActivities = selectedGuest && activitiesAtSlot.length > 0;
                        
                        return (
                      <td key={`${d}-${label}`} className="relative border p-0">
                            {bookingsAtSlot.map((b) => {
                            const [sh, sm] = b.startTime.split(':').map(Number);
                            const [eh, em] = b.endTime.split(':').map(Number);
                            const dur = eh * 60 + em - (sh * 60 + sm);
                            // Use exact height based on actual duration, not rounded to slots
                            const rows = Math.max(1, dur / SLOT_MINUTES);
                            const colors = colorForTreatment(b.treatment);
                            return (
                              <div key={b.bookingId || `${b.guestName}-${b.startTime}-${b.room}`}
                                   className="absolute left-0 right-0 px-1 py-0.5 text-[11px] overflow-hidden"
                                   style={{ height: `${rows * SLOT_HEIGHT}px`, top: 0, backgroundColor: colors.bg, border: `1px solid ${colors.bd}` }}
                                   onClick={() => setSelectedBooking({ ...b, date: d })}
                              >
                                <div className="truncate">{b.treatment}</div>
                                {viewMode !== 'compact' && (
                                  <div className="text-[10px] text-gray-600 truncate">{b.room} • {b.therapist}</div>
                                )}
                              </div>
                            );
                          })}
                            
                            {/* Show available activities in empty slots */}
                            {!hasBookings && hasAvailableActivities && (
                              <div
                                className="absolute inset-0 m-0.5 px-1 py-0.5 text-[11px] overflow-hidden cursor-pointer"
                                style={{
                                  backgroundColor: '#fef3c7',
                                  border: '2px dashed #f59e0b',
                                  borderRadius: '3px'
                                }}
                                onClick={() => {
                                  setSelectedSlotInfo({
                                    date: d,
                                    time: label,
                                    activities: activitiesAtSlot
                                  });
                                  setShowAvailableActivitiesModal(true);
                                }}
                                title={`${activitiesAtSlot.length} available activities - click to view`}
                              >
                                <div className="font-semibold text-center truncate text-amber-700">
                                  📅 {activitiesAtSlot.length} {activitiesAtSlot.length === 1 ? 'Activity' : 'Activities'}
                                </div>
                                {viewMode !== 'compact' && (
                                  <div className="text-[9px] text-center text-amber-600 truncate">
                                    Available
                                  </div>
                                )}
                              </div>
                            )}
                      </td>
                        );
                      })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Paper>
      <EditBookingModal
        booking={selectedBooking}
        config={config}
        show={Boolean(selectedBooking)}
        onClose={() => setSelectedBooking(null)}
        onDelete={(booking) => removeBooking(booking)}
        onSchedulingComplete={async () => {
          setSelectedBooking(null);
          await refreshBookings();
        }}
      />

      <Dialog open={showAddService} onClose={() => !addServiceLoading && setShowAddService(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add service{selectedGuest?.name ? ` — ${selectedGuest.name}` : ''}</DialogTitle>
        <DialogContent sx={{ paddingTop: '20px !important' }}>
          <IndividualBookingForm
            initialFormData={{
              guestName: selectedGuest?.name || '',
              guestId: selectedGuest?.guestId || selectedGuest?.id || '',
              treatment: '',
              preferredTime: '',
              preferredTimeOfDay: 'morning',
              therapistPreference: '',
              roomPreference: '',
              date: getGuestStayDates(selectedGuest).checkIn || new Date().toISOString().split('T')[0],
              genderPreference: '',
              avoidTimes: [],
              generalInstruction: '',
              treatmentInstruction: '',
            }}
            onSubmit={submitExtraService}
            config={config}
            isLoading={addServiceLoading}
            error={addServiceError}
            success=""
            isVisible={showAddService}
            externalMode
            lockedGuestId={selectedGuest?.guestId || selectedGuest?.id || ''}
            lockedGuestName={selectedGuest?.name || ''}
          />
          {addServicePreview && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {addServicePreview.treatment}
              </Typography>
              <Typography variant="body2">
                {addServicePreview.date} • {addServicePreview.startTime}–{addServicePreview.endTime}
              </Typography>
              <Typography variant="body2">
                {addServicePreview.therapist} • {addServicePreview.room}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddService(false)} disabled={addServiceLoading}>Close</Button>
          <Button onClick={confirmExtraService} disabled={!addServicePreview || addServiceLoading} variant="contained" sx={{ bgcolor: '#E4C984', color: '#171717' }}>
            Confirm booking
          </Button>
        </DialogActions>
      </Dialog>

      {/* Available Activities Enrollment Modal */}
      {showAvailableActivitiesModal && selectedSlotInfo && selectedGuest && (
        <Dialog 
          open={showAvailableActivitiesModal} 
          onClose={() => setShowAvailableActivitiesModal(false)} 
          fullWidth 
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: 2,
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: '#E4C984',
              color: '#171717',
              p: 2.5,
              position: 'relative'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: '#171717' }}>
              Available Activities
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(23,23,23,0.75)' }}>
              {selectedGuest.name} • {selectedSlotInfo.date} at {selectedSlotInfo.time}
            </Typography>
            <IconButton
              onClick={() => setShowAvailableActivitiesModal(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: '#171717'
              }}
            >
              <ClearIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedSlotInfo.activities.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No activities available at this time</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                {selectedSlotInfo.activities.map((activity) => {
                  // Calculate duration from start and end time if not provided
                  let defaultDuration = activity.duration;
                  if (!defaultDuration && activity.startTime && activity.endTime) {
                    const [sh, sm] = activity.startTime.split(':').map(Number);
                    const [eh, em] = activity.endTime.split(':').map(Number);
                    defaultDuration = (eh * 60 + em) - (sh * 60 + sm);
                  }
                  const activityDuration = activityDurations[activity.id] || defaultDuration || 60;
                  const capacityPercent = activity.maxCapacity > 0 
                    ? Math.round((activity.currentEnrollment / activity.maxCapacity) * 100) 
                    : 0;
                  const isFull = activity.currentEnrollment >= activity.maxCapacity;

                  return (
                    <Paper
                      key={activity.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: isFull ? '#fee2e2' : '#e5e7eb',
                        borderRadius: 2,
                        bgcolor: isFull ? '#fef2f2' : '#fff',
                        opacity: isFull ? 0.7 : 1
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#171717' }}>
                            {activity.activityName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {activity.startTime} - {activity.endTime} • {activity.roomName}
                          </Typography>
                          {activity.instructorName && (
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Instructor: {activity.instructorName}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600, 
                              color: isFull ? '#dc2626' : capacityPercent > 80 ? '#f59e0b' : '#16a34a'
                            }}
                          >
                            {activity.currentEnrollment}/{activity.maxCapacity}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {isFull ? 'Full' : `${capacityPercent}% filled`}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Duration selector */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 70 }}>
                          Duration:
                        </Typography>
                        <ButtonGroup size="small" variant="outlined">
                          {[30, 45, 60, 90].map((dur) => (
                            <Button
                              key={dur}
                              onClick={() => setActivityDurations(prev => ({ ...prev, [activity.id]: dur }))}
                              variant={activityDuration === dur ? 'contained' : 'outlined'}
                              sx={{
                                bgcolor: activityDuration === dur ? '#E4C984' : 'transparent',
                                color: '#171717',
                                borderColor: '#E4C984',
                                '&:hover': {
                                  bgcolor: activityDuration === dur ? '#d4b974' : 'rgba(228, 201, 132, 0.1)',
                                  borderColor: '#E4C984'
                                }
                              }}
                            >
                              {dur}m
                            </Button>
                          ))}
                        </ButtonGroup>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                          variant="contained"
                          disabled={isFull}
                          onClick={async () => {
                            try {
                              // Calculate end time based on selected duration
                              const [sh, sm] = activity.startTime.split(':').map(Number);
                              const endMinutes = sh * 60 + sm + activityDuration;
                              const endH = Math.floor(endMinutes / 60);
                              const endM = endMinutes % 60;
                              const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
                              
                              const response = await apiPost('/api/schedule/confirm', {
                                guestName: selectedGuest.name,
                                guestId: selectedGuest.id || selectedGuest.guestId,
                                treatment: activity.activityName,
                                date: selectedSlotInfo.date,
                                startTime: activity.startTime,
                                endTime: endTime,
                                therapist: activity.instructorName,
                                room: activity.roomName,
                                activityTimetableId: activity.id,
                                generalInstruction: '',
                                treatmentInstruction: `Booked for ${activityDuration} minutes`
                              });
                              
                              if (response.ok) {
                                setShowAvailableActivitiesModal(false);
                                // Refresh bookings
                                const entries = await Promise.all(
                                  dateRange.map(async (d) => {
                                    const res = await apiGet(`/api/bookings/${d}`);
                                    const data = await res.json();
                                    return [d, Array.isArray(data) ? data : []];
                                  })
                                );
                                const map = {};
                                for (const [d, list] of entries) map[d] = list;
                                setBookingsByDate(map);
                                // Refresh available activities
                                fetchAvailableActivitiesForRange();
                              } else {
                                const errData = await response.json();
                                alert(errData.detail || 'Failed to book activity');
                              }
                            } catch (error) {
                              console.error('Error booking activity:', error);
                              alert('Failed to book activity');
                            }
                          }}
                          sx={{
                            bgcolor: '#E4C984',
                            color: '#171717',
                            '&:hover': { bgcolor: '#d4b974' },
                            '&:disabled': { bgcolor: '#e5e7eb', color: '#9ca3af' }
                          }}
                        >
                          {isFull ? 'Full' : 'Enroll'}
                        </Button>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
            <Button 
              onClick={() => setShowAvailableActivitiesModal(false)}
              sx={{ color: '#171717' }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default Itinerary;
